import { useRouter } from 'next/router';

import { api } from '@/levity/api';
import { copy } from '@/levity/flows/copy';
import {
  addNodesToFlowCache,
  deleteNodesFromFlowCache,
  replaceFlowNodes,
  updateFlowCache,
  urls,
} from '@/levity/flows/core';
import { FlowNodesChangeset } from '@/levity/flows/types';
import { IntegrationAuthApp } from '@/levity/integrations/types';
import { ToastType, useToast } from '@/ui-library';

export const useCloneTemplate = () => {
  const router = useRouter();

  const { showToast } = useToast();

  const mutation = api.flows.cloneTemplate.useMutation({
    onSuccess: ({ id }) => router.push(urls.single(id)),
    onError: () =>
      showToast({
        title: copy.templates.clone.error,
        type: ToastType.Error,
      }),
  });

  const clone = (integration: IntegrationAuthApp, templateName: string, source = 'InApp') => {
    mutation.mutate({
      integration,
      templateName,
      source,
    });
  };

  return { clone, isCloning: mutation.isLoading };
};

export const useDuplicateFlow = () => {
  const utils = api.useUtils();

  const { showToast } = useToast();

  const mutation = api.flows.duplicateOne.useMutation({
    onSuccess: () => utils.flows.getAll.invalidate(),
    onError: () =>
      showToast({
        title: copy.duplicate.error,
        type: ToastType.Error,
      }),
  });

  const duplicateFlow = (id: string) => {
    mutation.mutate({ id });
  };

  return { duplicateFlow, isDuplicatingFlow: mutation.isLoading };
};

/**
 * Custom hook to handle adding nodes in a flow.
 * Optimistically updates the flow cache upon mutation.
 *
 * @param flowId - The ID of the flow where nodes are being added.
 * @param utils - The TRPC utils for cache management.
 * @returns A mutation object for adding nodes.
 */
export const useAddNodesMutation = (flowId: string, utils: ReturnType<typeof api.useUtils>) => {
  return api.flows.addNodes.useMutation({
    onMutate: (data) => {
      const previousFlowData = utils.flows.getOne.getData({ id: flowId });

      if (!previousFlowData) return;

      // Optimistically update the flow data by adding the new nodes
      const nextFlowData = addNodesToFlowCache(previousFlowData, data.nodes);

      // Update the cache with the new flow data
      utils.flows.getOne.setData({ id: flowId }, nextFlowData);

      // Return the previous flow data to restore on error
      return { previousFlowData };
    },

    onError: (_error, _variables, context) => {
      // Restore the previous flow data in case of an error
      if (context?.previousFlowData) {
        utils.flows.getOne.setData({ id: flowId }, context.previousFlowData);
      }
    },

    onSuccess: (data) => {
      const previousFlowData = utils.flows.getOne.getData({ id: flowId });

      if (previousFlowData && data.length > 0) {
        const nextFlowData = replaceFlowNodes(previousFlowData, data);
        utils.flows.getOne.setData(
          { id: flowId },
          nextFlowData as unknown as Awaited<ReturnType<typeof utils.flows.getOne.getData>>,
        );
      }

      utils.flows.nodeInputs.getAllForNode.invalidate();
      utils.flows.getAvailableOperationInputs.invalidate();
      utils.flows.nodeInputs.getAll.invalidate();
    },
  });
};

/**
 * Custom hook to handle updating nodes in a flow.
 * Optimistically updates the flow cache upon mutation.
 *
 * @param flowId - The ID of the flow where nodes are being updated.
 * @param utils - The TRPC utils for cache management.
 * @returns A mutation object for updating nodes.
 */
export const useUpdateNodesMutation = (flowId: string, utils: ReturnType<typeof api.useUtils>) => {
  const { showToast } = useToast();

  return api.flows.updateNodes.useMutation({
    onMutate: (changeset) => {
      const previousFlowData = utils.flows.getOne.getData({ id: flowId });

      if (!previousFlowData) return;

      const nextFlowData = updateFlowCache(previousFlowData, changeset as FlowNodesChangeset);

      utils.flows.getOne.setData({ id: flowId }, nextFlowData);

      return { previousFlowData };
    },
    onError: (_error, _changeset, context) => {
      utils.flows.getOne.setData({ id: flowId }, context?.previousFlowData);

      showToast({
        title: copy.update.error,
        type: ToastType.Error,
      });
    },
    onSuccess: (data) => {
      const previousFlowData = utils.flows.getOne.getData({ id: flowId });

      if (previousFlowData && data.length > 0) {
        const nextFlowData = replaceFlowNodes(previousFlowData, data);
        utils.flows.getOne.setData(
          { id: flowId },
          nextFlowData as unknown as Awaited<ReturnType<typeof utils.flows.getOne.getData>>,
        );
      }

      utils.flows.nodeInputs.getAllForNode.invalidate();
      utils.flows.getAvailableOperationInputs.invalidate();
      utils.flows.nodeInputs.getAll.invalidate();
    },
  });
};

/**
 * Custom hook to handle deleting nodes in a flow.
 * Optimistically updates the flow cache upon mutation.
 *
 * @param flowId - The ID of the flow where nodes are being deleted.
 * @param utils - The TRPC utils for cache management.
 * @returns A mutation object for deleting nodes.
 */
export const useDeleteNodesMutation = (flowId: string, utils: ReturnType<typeof api.useUtils>) => {
  return api.flows.deleteNodes.useMutation({
    onMutate: (variables) => {
      const previousFlowData = utils.flows.getOne.getData({ id: flowId });

      if (!previousFlowData) return;

      // Optimistically update the flow data by removing the nodes
      const nextFlowData = deleteNodesFromFlowCache(previousFlowData, variables.nodeIds);

      // Update the cache with the new flow data
      utils.flows.getOne.setData({ id: flowId }, nextFlowData);

      // Return the previous flow data to restore on error
      return { previousFlowData };
    },
    onError: (_error, _variables, context) => {
      // Restore the previous flow data in case of an error
      if (context?.previousFlowData) {
        utils.flows.getOne.setData({ id: flowId }, context.previousFlowData);
      }
    },
    onSuccess: () => {
      utils.flows.nodeInputs.getAllForNode.invalidate();
      utils.flows.getAvailableOperationInputs.invalidate();
      utils.flows.nodeInputs.getAll.invalidate();
    },
  });
};
